class CaseStylePicker extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-case-style-overlay]');
    this.canvas = this.overlay?.querySelector('[data-case-style-canvas]');
    this.transformBox = this.overlay?.querySelector('[data-case-style-transform]');
    this.transformHandles = [...(this.overlay?.querySelectorAll('[data-case-style-handle]') || [])];
    this.context = this.canvas?.getContext('2d');
    this.maskCanvas = document.createElement('canvas');
    this.maskContext = this.maskCanvas.getContext('2d');
    this.styleCanvas = document.createElement('canvas');
    this.styleContext = this.styleCanvas.getContext('2d');
    this.styleMaskCanvas = document.createElement('canvas');
    this.styleMaskContext = this.styleMaskCanvas.getContext('2d');
    this.controls = this.overlay?.querySelector('[data-case-style-controls]');
    this.confirmDialog = this.querySelector('[data-case-style-confirm-dialog]');
    this.confirmDialogImage = this.confirmDialog?.querySelector('[data-case-style-confirm-image]');
    this.loading = this.overlay?.querySelector('[data-case-style-loading]');
    this.loadingMessage = this.loading?.querySelector('[data-case-style-loading-message]');
    this.label = this.querySelector('[data-case-style-label]');
    this.options = [...this.querySelectorAll('[data-case-style-option]:not(:disabled)')];
    this.maskUrls = this.parseMaskUrls();
    this.productInformation = this.closest('.product-information');
    this.media = this.productInformation?.querySelector('.product-information__media');
    this.layers = {
      style: { url: '', image: null, scale: 0.85, scaleX: 0.85, scaleY: 0.85, offsetX: 0, offsetY: 0 },
      generated: { url: '', image: null, scale: 1, scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 },
    };
    this.activeLayer = 'generated';
    this.maskColor = 'orange';
    this.pixelRatio = 1;
    this.transformVisible = true;
    this.drawFramePending = false;
    this.styleMaskCacheKey = '';
    this.generatedInitialScale = 1;
    this.backplateBounds = null;
    this.generatedFrame = null;
    this.historySelectionRequestId = 0;
    this.historySelectionTimer = null;
    this.lastSavedDesignUrl = '';
    this.addingToCart = false;

    this.styleCanvasReady = Boolean(this.media && this.overlay && this.canvas && this.context && this.options.length);
    this.applyInitialStyleSelection();
    this.addEventListener('click', this.handleStyleClick, true);
    this.addEventListener('change', this.handleChange);
    if (!this.styleCanvasReady) return;
    document.addEventListener('change', this.handleUploadChange);
    this.productInformation?.addEventListener('casecase:canvas-image-selected', this.handleCanvasImageSelected);
    document.addEventListener('change', this.handleVariantChange);
    document.addEventListener('canvas-mask-color-change', this.handleMaskColorChange);
    this.canvas.addEventListener('pointerdown', this.handlePointerDown);
    this.canvas.addEventListener('pointermove', this.handlePointerMove);
    this.canvas.addEventListener('pointerup', this.handlePointerUp);
    this.canvas.addEventListener('pointercancel', this.handlePointerUp);
    this.canvas.addEventListener('dblclick', this.handleCanvasDoubleClick);
    this.transformBox?.addEventListener('pointerdown', this.handleTransformPointerDown);
    this.transformBox?.addEventListener('pointermove', this.handleTransformPointerMove);
    this.transformBox?.addEventListener('pointerup', this.handleTransformPointerUp);
    this.transformBox?.addEventListener('pointercancel', this.handleTransformPointerUp);
    this.canvas.addEventListener('keydown', this.handleKeyDown);
    this.overlay.addEventListener('click', this.handleControlClick);
    this.confirmDialog?.addEventListener('click', this.handleConfirmDialogClick);
    this.confirmDialog?.addEventListener('cancel', this.handleConfirmDialogCancel);
    this.canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    this.canvas.addEventListener('wheel', this.handleWheel, { passive: false });
    this.resizeObserver = new ResizeObserver(this.resizeCanvas);
    this.resizeObserver.observe(this.media);
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const selectedOption = this.applyInitialStyleSelection();
      if (selectedOption) this.setStyle(selectedOption);
      this.syncGeneratedImage();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleStyleClick, true);
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('change', this.handleUploadChange);
    this.productInformation?.removeEventListener('casecase:canvas-image-selected', this.handleCanvasImageSelected);
    document.removeEventListener('change', this.handleVariantChange);
    document.removeEventListener('canvas-mask-color-change', this.handleMaskColorChange);
    this.canvas?.removeEventListener('pointerdown', this.handlePointerDown);
    this.canvas?.removeEventListener('pointermove', this.handlePointerMove);
    this.canvas?.removeEventListener('pointerup', this.handlePointerUp);
    this.canvas?.removeEventListener('pointercancel', this.handlePointerUp);
    this.canvas?.removeEventListener('dblclick', this.handleCanvasDoubleClick);
    this.transformBox?.removeEventListener('pointerdown', this.handleTransformPointerDown);
    this.transformBox?.removeEventListener('pointermove', this.handleTransformPointerMove);
    this.transformBox?.removeEventListener('pointerup', this.handleTransformPointerUp);
    this.transformBox?.removeEventListener('pointercancel', this.handleTransformPointerUp);
    this.canvas?.removeEventListener('keydown', this.handleKeyDown);
    this.overlay?.removeEventListener('click', this.handleControlClick);
    this.confirmDialog?.removeEventListener('click', this.handleConfirmDialogClick);
    this.confirmDialog?.removeEventListener('cancel', this.handleConfirmDialogCancel);
    this.canvas?.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas?.removeEventListener('touchmove', this.handleTouchMove);
    this.canvas?.removeEventListener('touchend', this.handleTouchEnd);
    this.canvas?.removeEventListener('wheel', this.handleWheel);
    window.clearTimeout(this.historySelectionTimer);
    this.resizeObserver?.disconnect();
    if (this.generatedObjectUrl) URL.revokeObjectURL(this.generatedObjectUrl);
    Object.values(this.layers || {}).forEach((layer) => {
      if (layer.objectUrl) URL.revokeObjectURL(layer.objectUrl);
    });
    this.overlay?.remove();
  }

  getStyleProductHandle(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== new URL(window.location.href).origin) return '';
      const handle = url.pathname.match(/\/products\/([^/]+)/)?.[1];
      return handle ? decodeURIComponent(handle) : '';
    } catch {
      return '';
    }
  }

  getCurrentProductStyleOptions() {
    const currentHandle = this.getStyleProductHandle(window.location.href);
    if (currentHandle) {
      return this.options.filter((option) =>
        this.getStyleProductHandle(option.dataset.caseStyleProductUrl) === currentHandle);
    }
    const currentProductId = this.dataset.caseStyleCurrentProductId;
    return currentProductId
      ? this.options.filter((option) => option.dataset.caseStyleProductId === currentProductId)
      : [];
  }

  getInitialStyleOption() {
    const currentOptions = this.getCurrentProductStyleOptions();
    const requestedToken = new URL(window.location.href).searchParams.get('case_style');
    if (currentOptions.length) {
      return currentOptions.find((option) => requestedToken && option.dataset.caseStyleToken === requestedToken)
        || currentOptions[0];
    }
    const requestedOption = requestedToken
      ? this.options.find((option) => option.dataset.caseStyleToken === requestedToken
        && !option.dataset.caseStyleProductUrl && !option.dataset.caseStyleProductId)
      : null;
    return requestedOption || this.options.find((option) => option.checked) || this.options[0];
  }

  applyInitialStyleSelection() {
    const selectedOption = this.getInitialStyleOption();
    if (!selectedOption) return null;
    this.options.forEach((option) => {
      const selected = option === selectedOption;
      option.defaultChecked = selected;
      option.checked = selected;
    });
    if (this.label) this.label.textContent = selectedOption.dataset.caseStyleLabel || selectedOption.value;
    this.selectedStyle = selectedOption.value;
    return selectedOption;
  }

  getStyleProductDestination(option) {
    if (!option || option.disabled) return null;
    const productUrl = option.dataset.caseStyleProductUrl;
    if (!productUrl) return null;
    try {
      const destination = new URL(productUrl, window.location.href);
      const current = new URL(window.location.href);
      if (!['http:', 'https:'].includes(destination.protocol) || destination.origin !== current.origin) return null;
      const currentHandle = this.getStyleProductHandle(current.href);
      const destinationHandle = this.getStyleProductHandle(destination.href);
      if (destination.pathname.replace(/\/$/, '') === current.pathname.replace(/\/$/, '')
        || (destinationHandle && destinationHandle === currentHandle)) return null;
      if (!currentHandle && option.dataset.caseStyleProductId
        && option.dataset.caseStyleProductId === this.dataset.caseStyleCurrentProductId) return null;
      if (option.dataset.caseStyleToken) destination.searchParams.set('case_style', option.dataset.caseStyleToken);
      return destination.href;
    } catch {
      return null;
    }
  }

  handleStyleClick = (event) => {
    if (event.button !== 0) return;
    const label = event.target.closest?.('label.case-style-option');
    const option = label?.querySelector('[data-case-style-option]');
    if (!this.options.includes(option)) return;
    const destination = this.getStyleProductDestination(option);
    if (!destination) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(destination);
  };

  handleChange = (event) => {
    if (!this.options.includes(event.target)) return;
    event.stopPropagation();
    const destination = this.getStyleProductDestination(event.target);
    if (destination) {
      window.location.assign(destination);
      return;
    }
    if (this.styleCanvasReady) this.setStyle(event.target);
    else if (this.label) this.label.textContent = event.target.dataset.caseStyleLabel || event.target.value;
  };

  handleUploadChange = (event) => {
    if (!event.target.matches('[data-ai-generated-image-input], [data-ai-generated-display-input]')) return;
    if (event.target.closest('.product-information') !== this.productInformation) return;
    this.syncGeneratedImage();
  };

  handlePointerDown = (event) => {
    if (!this.isEditableLayer(this.activeLayer)) return;
    const layer = this.layers[this.activeLayer];
    if (!this.transformVisible || !layer.image?.complete || !layer.image.naturalWidth) return;
    this.draggingPointer = event.pointerId;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.focus({ preventScroll: true });
    event.preventDefault();
  };

  handlePointerMove = (event) => {
    if (this.draggingPointer !== event.pointerId) return;
    const layer = this.layers[this.activeLayer];
    layer.offsetX += event.clientX - this.pointerX;
    layer.offsetY += event.clientY - this.pointerY;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.scheduleDraw();
  };

  handlePointerUp = (event) => {
    if (this.draggingPointer !== event.pointerId) return;
    this.draggingPointer = null;
    this.canvas.releasePointerCapture(event.pointerId);
  };

  handleCanvasDoubleClick = (event) => {
    if (!this.isEditableLayer(this.activeLayer)) return;
    const bounds = this.bounds?.[this.activeLayer];
    if (!bounds || this.overlay.hidden) return;
    const rect = this.overlay.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) return;
    this.transformVisible = true;
    this.updateTransformBox();
    event.preventDefault();
  };

  handleTransformPointerDown = (event) => {
    if (!this.isEditableLayer(this.activeLayer)) return;
    const handle = event.target.closest('[data-case-style-handle]');
    const layer = this.layers[this.activeLayer];
    const bounds = this.bounds?.[this.activeLayer];
    if (!handle || !bounds || !layer.image?.complete || !layer.image.naturalWidth) return;
    const direction = handle.dataset.caseStyleHandle;
    this.resizePointer = event.pointerId;
    this.resizeStart = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      direction,
      scale: layer.scale,
      scaleX: layer.scaleX ?? layer.scale,
      scaleY: layer.scaleY ?? layer.scale,
      bounds,
      baseWidth: bounds.width / (layer.scaleX ?? layer.scale),
      baseHeight: bounds.height / (layer.scaleY ?? layer.scale),
      referenceCenterX: this.generatedFrame ? this.generatedFrame.left + this.generatedFrame.width / 2 : this.canvas.clientWidth / 2,
      referenceCenterY: this.generatedFrame ? this.generatedFrame.top + this.generatedFrame.height / 2 : this.canvas.clientHeight / 2,
    };
    handle.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  handleTransformPointerMove = (event) => {
    if (this.resizePointer !== event.pointerId || !this.resizeStart) return;
    const start = this.resizeStart;
    const layer = this.layers[this.activeLayer];
    const direction = start.direction;
    const horizontal = direction.includes('e') || direction.includes('w');
    const vertical = direction.includes('n') || direction.includes('s');
    const anchorX = direction.includes('w') ? start.bounds.right : start.bounds.left;
    const anchorY = direction.includes('n') ? start.bounds.bottom : start.bounds.top;
    const overlayRect = this.overlay.getBoundingClientRect();
    const pointerX = event.clientX - overlayRect.left;
    const pointerY = event.clientY - overlayRect.top;
    const width = horizontal ? Math.abs(pointerX - anchorX) : start.bounds.width;
    const height = vertical ? Math.abs(pointerY - anchorY) : start.bounds.height;
    const widthScale = width / start.baseWidth;
    const heightScale = height / start.baseHeight;
    const minScale = this.generatedInitialScale * 0.5;
    const maxScale = this.generatedInitialScale * 2;
    const requestedScale = horizontal && vertical
      ? Math.max(widthScale, heightScale)
      : (horizontal ? widthScale : (vertical ? heightScale : start.scale));
    const scale = Math.min(maxScale, Math.max(minScale, requestedScale));
    const nextWidth = start.baseWidth * scale;
    const nextHeight = start.baseHeight * scale;
    const left = direction.includes('w') ? anchorX - nextWidth : (direction.includes('e') ? anchorX : start.bounds.left + (start.bounds.width - nextWidth) / 2);
    const top = direction.includes('n') ? anchorY - nextHeight : (direction.includes('s') ? anchorY : start.bounds.top + (start.bounds.height - nextHeight) / 2);
    const centerX = left + nextWidth / 2;
    const centerY = top + nextHeight / 2;
    layer.scaleX = scale;
    layer.scaleY = scale;
    layer.scale = scale;
    layer.offsetX = centerX - start.referenceCenterX;
    layer.offsetY = centerY - start.referenceCenterY;
    this.scheduleDraw();
    event.preventDefault();
  };

  handleTransformPointerUp = (event) => {
    if (this.resizePointer !== event.pointerId) return;
    this.resizePointer = null;
    this.resizeStart = null;
  };

  handleKeyDown = (event) => {
    if (!this.isEditableLayer(this.activeLayer)) return;
    const layer = this.layers[this.activeLayer];
    if (!layer.image?.complete || !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    this.transformVisible = true;
    const distance = event.shiftKey ? 20 : 5;
    if (event.key === 'ArrowUp') layer.offsetY -= distance;
    if (event.key === 'ArrowDown') layer.offsetY += distance;
    if (event.key === 'ArrowLeft') layer.offsetX -= distance;
    if (event.key === 'ArrowRight') layer.offsetX += distance;
    event.preventDefault();
    this.scheduleDraw();
  };

  handleControlClick = async (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.matches('[data-case-style-transform-done], [data-case-style-confirm]')) {
      if (this.savingCanvasImage) return;
      const isConfirm = button.matches('[data-case-style-confirm]');
      if (isConfirm && !this.transformVisible) {
        this.transformVisible = true;
        this.updateTransformBox();
        return;
      }
      const saved = await this.saveCanvasImage(button);
      // Confirm 保存成功后弹出确认框, 让用户直接加购或去结账
      if (saved && isConfirm) this.openConfirmDialog();
      return;
    }
    if (button.matches('[data-case-style-reset]')) {
      this.resetLayer('generated');
      this.transformVisible = true;
      this.updateTransformBox();
      return;
    }
    if (button.matches('[data-case-style-clear]')) {
      this.clearGeneratedImage();
      return;
    }
    if (button.matches('[data-case-style-zoom-in], [data-case-style-zoom-out]')) {
      this.transformVisible = true;
      const layer = this.layers.generated;
      const delta = button.matches('[data-case-style-zoom-in]') ? 0.1 : -0.1;
      const scale = Math.min(this.generatedInitialScale * 2, Math.max(this.generatedInitialScale * 0.5, (layer.scale ?? this.generatedInitialScale) + delta));
      layer.scale = scale;
      layer.scaleX = scale;
      layer.scaleY = scale;
      this.scheduleDraw();
    }
  };

  parseMaskUrls() {
    try {
      const urls = JSON.parse(this.querySelector('[data-case-style-mask-urls]')?.textContent || '{}');
      delete urls.__end;
      return urls;
    } catch (error) {
      console.error('[CaseStylePicker] Invalid mask asset URL map.', error);
      return {};
    }
  }

  handleCanvasImageSelected = (event) => {
    const url = event.detail?.url || event.detail?.sourceUrl || '';
    if (!url) return;
    if (this.generatedObjectUrl && this.generatedObjectUrl !== url) {
      URL.revokeObjectURL(this.generatedObjectUrl);
    }
    this.generatedObjectUrl = event.detail?.objectUrl ? url : '';
    this.layers.generated.url = url;
    this.resetLayer('generated');
    this.activeLayer = 'generated';
    this.transformVisible = true;
    const fallbackUrl = event.detail?.sourceUrl && event.detail.sourceUrl !== url
      ? event.detail.sourceUrl
      : '';
    const requestId = ++this.historySelectionRequestId;
    this.setCanvasLoading(true);
    this.loadImage('generated', url, fallbackUrl, (loaded, error) => {
      if (requestId !== this.historySelectionRequestId) return;
      if (!loaded) {
        this.finishCanvasLoading(false, error);
        return;
      }
      this.waitForHistoryCanvasReady(requestId);
    });
    this.updateVisibility();
    this.scrollToEditorOnMobile();
  };

  setCanvasLoading(loading, message = 'Loading your design...') {
    if (!this.loading) return;
    this.loading.hidden = !loading;
    if (this.loadingMessage) this.loadingMessage.textContent = message;
    this.overlay?.setAttribute('aria-busy', String(loading));
  }

  finishCanvasLoading(success, error) {
    window.clearTimeout(this.historySelectionTimer);
    this.setCanvasLoading(false);
    if (success) return;
    const message = 'We couldn’t load this design. Please choose it again or try another image.';
    console.error('[CaseStylePicker] Failed to render selected history image.', {
      error,
      generatedUrl: this.layers.generated.url,
      styleUrl: this.layers.style.url,
      generatedReady: Boolean(this.layers.generated.image?.complete && this.layers.generated.image?.naturalWidth),
      styleReady: Boolean(this.layers.style.image?.complete && this.layers.style.image?.naturalWidth),
    });
    if (typeof MessageToast !== 'undefined' && typeof MessageToast.error === 'function') {
      MessageToast.error(message, 3000);
    }
  }

  waitForHistoryCanvasReady(requestId, startedAt = performance.now()) {
    if (requestId !== this.historySelectionRequestId) return;
    const generated = this.layers.generated.image;
    const style = this.layers.style.image;
    if (generated?.complete && generated.naturalWidth && style?.complete && style.naturalWidth) {
      this.finishCanvasLoading(true);
      this.scheduleDraw();
      return;
    }
    if (performance.now() - startedAt >= 10000) {
      this.finishCanvasLoading(false, new Error('Canvas images did not finish loading in time.'));
      return;
    }
    this.historySelectionTimer = window.setTimeout(() => this.waitForHistoryCanvasReady(requestId, startedAt), 50);
  }

  scrollToEditorOnMobile() {
    if (!window.matchMedia?.('(max-width: 749px)').matches) return;
    window.requestAnimationFrame(() => {
      this.overlay?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  handleVariantChange = (event) => {
    if (!event.target.matches('variant-picker input, variant-picker select')) return;
    this.clearGeneratedImage();
    this.updateMaskFromMapping();
  };

  handleMaskColorChange = (event) => {
    if (event.target.closest('.product-information') !== this.productInformation) return;
    this.maskColor = event.detail?.color || 'orange';
    this.updateMaskFromMapping();
  };

  getTouchDistance(touches) {
    const [a, b] = touches;
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  }

  clampGeneratedPosition(generated = this.bounds?.generated, mask = this.bounds?.style) {
    const layer = this.layers.generated;
    if (!generated || !mask || !layer.image?.naturalWidth) return false;
    const minimumVisibleRatio = 0.5;
    const minOffsetX = mask.left + generated.width * minimumVisibleRatio - generated.right;
    const maxOffsetX = mask.right - generated.width * minimumVisibleRatio - generated.left;
    const minOffsetY = mask.top + generated.height * minimumVisibleRatio - generated.bottom;
    const maxOffsetY = mask.bottom - generated.height * minimumVisibleRatio - generated.top;
    const correctionX = minOffsetX <= maxOffsetX
      ? Math.min(maxOffsetX, Math.max(minOffsetX, 0))
      : (minOffsetX + maxOffsetX) / 2;
    const correctionY = minOffsetY <= maxOffsetY
      ? Math.min(maxOffsetY, Math.max(minOffsetY, 0))
      : (minOffsetY + maxOffsetY) / 2;
    const hasCorrection = Math.abs(correctionX) > 0.01 || Math.abs(correctionY) > 0.01;
    if (!hasCorrection) return false;
    layer.offsetX += correctionX;
    layer.offsetY += correctionY;
    return true;
  }

  handleTouchStart = (event) => {
    if (event.touches.length !== 2 || !this.isEditableLayer(this.activeLayer)) return;
    this.pinchStartDistance = this.getTouchDistance(event.touches);
    this.pinchStartScale = this.layers.generated.scale ?? this.generatedInitialScale;
    this.draggingPointer = null;
    event.preventDefault();
  };

  handleTouchMove = (event) => {
    if (event.touches.length !== 2 || !this.pinchStartDistance || !this.isEditableLayer(this.activeLayer)) return;
    this.transformVisible = true;
    const layer = this.layers.generated;
    const scale = Math.min(this.generatedInitialScale * 2, Math.max(this.generatedInitialScale * 0.5, this.pinchStartScale * this.getTouchDistance(event.touches) / this.pinchStartDistance));
    layer.scale = scale;
    layer.scaleX = scale;
    layer.scaleY = scale;
    this.scheduleDraw();
    event.preventDefault();
  };

  handleTouchEnd = (event) => {
    if (event.touches.length < 2) {
      this.pinchStartDistance = null;
      this.pinchStartScale = null;
    }
  };

  handleWheel = (event) => {
    if (!event.ctrlKey || !this.isEditableLayer(this.activeLayer)) return;
    this.transformVisible = true;
    const layer = this.layers.generated;
    const delta = event.deltaY > 0 ? -0.04 : 0.04;
    const scale = Math.min(this.generatedInitialScale * 2, Math.max(this.generatedInitialScale * 0.5, (layer.scale ?? this.generatedInitialScale) + delta));
    layer.scale = scale;
    layer.scaleX = scale;
    layer.scaleY = scale;
    this.scheduleDraw();
    event.preventDefault();
  };

  scheduleDraw = () => {
    if (this.drawFramePending) return;
    this.drawFramePending = true;
    requestAnimationFrame(() => {
      this.drawFramePending = false;
      this.draw();
    });
  };

  setStyle(option) {
    const layer = this.layers.style;
    layer.url = option.dataset.caseStyleImage || '';
    this.selectedStyle = option.value;
    this.updateMaskFromMapping();
    this.label.textContent = option.dataset.caseStyleLabel || option.value;
    this.transformVisible = true;
    this.loadImage('style', layer.url);
    if (this.layers.generated.url) this.activeLayer = 'generated';
    this.updateVisibility();
  }

  getSelectedVariantValues() {
    const picker = this.productInformation?.querySelector('variant-picker');
    const values = [...(picker?.querySelectorAll('input[type="radio"]:checked, select.variant-option__select') || [])]
      .map((input) => this.normalizeMappingKey(input.value));
    return values;
  }

  normalizeMappingKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-');
  }

  updateMaskFromMapping() {
    const values = this.getSelectedVariantValues();
    const isIphone16 = values.some((value) => value.includes('iphone-16'));
    const isIphone18 = values.some((value) => value.includes('iphone-18'));
    const series = isIphone16 ? '16' : (isIphone18 ? '18' : '17');
    const model = `iphone-${series}-pro${values.some((value) => value.includes('max')) ? '-max' : ''}`;
    const stand = values.some((value) => value.includes('stand')) ? 'stand' : 'no-stand';
    const color = values.some((value) => value.includes('clear')) ? 'white' : 'black';
    const availableStylesByModel = {
      'iphone-16-pro': ['gray', 'white', 'gold', 'black'],
      'iphone-16-pro-max': ['gray', 'white', 'gold', 'black'],
      'iphone-17-pro': ['orange', 'deep-blue', 'gray'],
      'iphone-17-pro-max': ['orange', 'deep-blue', 'gray'],
      'iphone-18-pro': ['black', 'silver', 'blue', 'red'],
      'iphone-18-pro-max': ['black', 'silver', 'blue', 'red'],
    };
    const availableStyles = availableStylesByModel[model] || availableStylesByModel['iphone-17-pro'];
    const selectedMaskColor = model.includes('iphone-18')
      ? String(this.maskColor || '').replace(/-18$/, '')
      : this.maskColor;
    const styleKey = availableStyles.includes(selectedMaskColor) ? selectedMaskColor : availableStyles[0];
    const maskAssetName = `mask-${stand}-${model}-${color}-${styleKey}.png`;
    const maskUrl = this.maskUrls[maskAssetName];
    if (!maskUrl) {
      console.error('[CaseStylePicker] Mask asset URL is missing.', maskAssetName);
      return;
    }
    if (this.layers.style.url === maskUrl) return;
    this.layers.style.url = maskUrl;
    this.resetLayer('style');
    this.loadImage('style', maskUrl);
  }

  syncGeneratedImage() {
    const uploadRoot = this.productInformation?.querySelector('[data-casecase-ai-upload]');
    const generatedUrl = uploadRoot?.querySelector('[data-ai-generated-image-input]')?.value?.trim() || '';
    const displayUrl = uploadRoot?.querySelector('[data-ai-generated-display-input]')?.value?.trim() || '';
    const nextUrl = displayUrl || generatedUrl;
    if (nextUrl === this.layers.generated.url) return;
    this.layers.generated.url = nextUrl;
    this.resetLayer('generated');
    this.loadImage('generated', nextUrl, generatedUrl !== nextUrl ? generatedUrl : '');
    if (nextUrl) {
      this.activeLayer = 'generated';
      this.transformVisible = true;
    }
    else if (this.layers.style.url) this.activeLayer = 'style';
    this.updateVisibility();
  }

  loadImage(layerName, url, fallbackUrl = '', onComplete = null) {
    const layer = this.layers[layerName];
    if (layerName === 'style') this.styleMaskCacheKey = '';
    layer.loadRequestId = (layer.loadRequestId || 0) + 1;
    const requestId = layer.loadRequestId;
    if (layer.objectUrl) {
      URL.revokeObjectURL(layer.objectUrl);
      layer.objectUrl = '';
    }
    if (!url) {
      layer.image = null;
      this.scheduleDraw();
      onComplete?.(false, new Error('Image URL is empty.'));
      return;
    }
    const image = new Image();
    const isRemoteGeneratedImage = layerName === 'generated' && /^https?:\/\//i.test(url);
    if (!isRemoteGeneratedImage) image.crossOrigin = 'anonymous';
    layer.image = image;
    const complete = (loaded, error) => {
      if (layer.loadRequestId !== requestId || layer.image !== image) return;
      onComplete?.(loaded, error);
    };
    image.onload = () => {
      if (layer.loadRequestId !== requestId || layer.image !== image) return;
      this.resizeCanvas();
      this.scheduleDraw();
      complete(true);
    };
    image.onerror = () => {
      if (layer.loadRequestId !== requestId || layer.image !== image) return;
      if (fallbackUrl && fallbackUrl !== url) {
        this.loadImage(layerName, fallbackUrl, '', onComplete);
        return;
      }
      layer.image = null;
      this.scheduleDraw();
      complete(false, new Error(`Failed to load ${url}`));
    };
    if (!isRemoteGeneratedImage) {
      image.src = url;
      return;
    }
    fetch(url, { mode: 'cors', cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`Image request failed with HTTP ${response.status}.`);
        return response.blob();
      })
      .then((blob) => {
        if (layer.loadRequestId !== requestId || layer.image !== image) return;
        layer.objectUrl = URL.createObjectURL(blob);
        image.src = layer.objectUrl;
      })
      .catch((error) => {
        if (layer.loadRequestId !== requestId || layer.image !== image) return;
        if (fallbackUrl && fallbackUrl !== url) {
          this.loadImage(layerName, fallbackUrl, '', onComplete);
          return;
        }
        layer.image = null;
        this.scheduleDraw();
        complete(false, error);
      });
  }

  clearGeneratedImage() {
    if (this.generatedObjectUrl) {
      URL.revokeObjectURL(this.generatedObjectUrl);
      this.generatedObjectUrl = '';
    }
    const uploadRoot = this.productInformation?.querySelector('[data-casecase-ai-upload]');
    const removeButton = uploadRoot?.querySelector('[data-ai-upload-remove]');
    if (removeButton) {
      removeButton.click();
      return;
    }
    this.layers.generated.url = '';
    this.resetLayer('generated');
    this.updateVisibility();
  }

  resetLayer(layerName) {
    const layer = this.layers[layerName];
    layer.scale = layerName === 'style' ? 0.85 : this.generatedInitialScale;
    layer.scaleX = layer.scale;
    layer.scaleY = layer.scale;
    layer.offsetX = 0;
    layer.offsetY = 0;
    this.draw();
  }

  async saveCanvasImage(button) {
    if (this.savingCanvasImage || !this.canvas) return false;
    const uploadRoot = this.productInformation?.querySelector('[data-casecase-ai-upload]');
    const status = uploadRoot?.querySelector('[data-ai-upload-status]');
    const saveButtons = this.overlay.querySelectorAll('[data-case-style-transform-done], [data-case-style-confirm]');
    const isMobile = typeof window !== 'undefined' && (
      window.matchMedia?.('(max-width: 749px)').matches || window.innerWidth <= 749
    );
    const toastDuration = 3000;
    const savingMessage = 'Saving your design...';
    const successMessage = 'Design saved! You can now add it to your cart.';
    let savingToastId = null;

    this.savingCanvasImage = true;
    saveButtons.forEach((saveButton) => {
      saveButton.disabled = true;
      saveButton.setAttribute('aria-busy', 'true');
      saveButton.classList.add('is-saving');
    });
    if (status) {
      status.textContent = savingMessage;
      status.className = 'ai-upload-status';
    }
    if (isMobile && typeof MessageToast !== 'undefined' && typeof MessageToast.info === 'function') {
      savingToastId = MessageToast.info(savingMessage, toastDuration);
    }

    try {
      const blob = await this.createExportBlob();
      const token = await this.getUploadToken();
      if (!token) throw new Error('Unable to initialize upload access. Refresh the page and try again.');

      const body = new FormData();
      body.append('image', new File([blob], 'casecase-design.jpg', { type: 'image/jpeg' }));
      const apiBase = window.THEME_CONFIG?.apiBaseUrl || 'https://alb.deliver3d.net:3000';
      const response = await fetch(`${String(apiBase).replace(/\/$/, '')}/export/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });

      let result;
      try {
        result = await response.json();
      } catch (error) {
        throw new Error('The upload service returned an invalid response.');
      }
      if (result?.data?.moderation?.limittype != null) {
        throw new Error('This image cannot be used. Please adjust the design and try again.');
      }
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || 'Upload request failed.');
      }

      const url = result?.data?.fileUrl || result?.url || result?.imageUrl || result?.image_url;
      if (!url) throw new Error('The upload response did not include an image URL.');
      this.replaceUploadPhoto(url, uploadRoot);
      this.lastSavedDesignUrl = url;
      this.transformVisible = false;
      this.updateTransformBox();
      if (status) {
        status.textContent = successMessage;
        status.className = 'ai-upload-status success';
      }
      if (savingToastId && typeof MessageToast !== 'undefined') {
        MessageToast.close(savingToastId);
        savingToastId = null;
      }
      if (isMobile && typeof MessageToast !== 'undefined' && typeof MessageToast.success === 'function') {
        MessageToast.success(successMessage, toastDuration);
      }
      return true;
    } catch (error) {
      const message = error?.name === 'SecurityError'
        ? 'Unable to save this design because one of its images blocks canvas export.'
        : (error?.message || 'Unable to save the design. Please try again.');
      if (status) {
        status.textContent = message;
        status.className = 'ai-upload-status error';
      }
      console.error('[CaseStylePicker] Unable to save canvas image.', error);
      if (savingToastId && typeof MessageToast !== 'undefined') {
        MessageToast.close(savingToastId);
        savingToastId = null;
      }
      if (typeof MessageToast !== 'undefined' && typeof MessageToast.error === 'function') {
        MessageToast.error(message, toastDuration);
      }
      return false;
    } finally {
      if (savingToastId && typeof MessageToast !== 'undefined') MessageToast.close(savingToastId);
      this.savingCanvasImage = false;
      saveButtons.forEach((saveButton) => {
        saveButton.disabled = false;
        saveButton.removeAttribute('aria-busy');
        saveButton.classList.remove('is-saving');
      });
    }
  }

  openConfirmDialog() {
    if (!this.confirmDialog) return;
    if (this.confirmDialogImage && this.lastSavedDesignUrl) {
      this.confirmDialogImage.src = this.lastSavedDesignUrl;
    }
    if (this.confirmDialog.open) return;
    if (typeof this.confirmDialog.showModal === 'function') {
      this.confirmDialog.showModal();
    } else {
      this.confirmDialog.setAttribute('open', '');
    }
  }

  closeConfirmDialog() {
    if (!this.confirmDialog?.open) return;
    if (typeof this.confirmDialog.close === 'function') {
      this.confirmDialog.close();
    } else {
      this.confirmDialog.removeAttribute('open');
    }
  }

  handleConfirmDialogCancel = () => {
    if (this.addingToCart) return;
    this.closeConfirmDialog();
  };

  handleConfirmDialogClick = async (event) => {
    // 点击 backdrop (dialog 元素本身) 关闭
    if (event.target === this.confirmDialog) {
      this.handleConfirmDialogCancel();
      return;
    }
    const button = event.target.closest('button');
    if (!button) return;
    if (button.matches('[data-case-style-confirm-close]')) {
      this.handleConfirmDialogCancel();
      return;
    }
    const checkout = button.matches('[data-case-style-confirm-checkout]');
    if (!checkout && !button.matches('[data-case-style-confirm-add]')) return;
    if (this.addingToCart) return;
    await this.addDesignToCart(button, { checkout });
  };

  async addDesignToCart(button, { checkout = false } = {}) {
    this.addingToCart = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const addToCartButton = this.findAddToCartButton();
      if (!addToCartButton) {
        this.notifyCartError('The Add to cart button could not be found on this page.');
        return;
      }
      // upload gate 在 casecase:canvas-design-saved 之后才解除禁用, 给它一点时间
      if (!await this.waitForAddToCartEnabled(addToCartButton)) {
        this.notifyCartError('This design is not ready to be added to the cart yet. Please try again.');
        return;
      }
      // 先关闭 modal, 避免 top layer 的 inert 影响主表单的加购流程
      this.closeConfirmDialog();
      const cartResult = this.waitForCartResult();
      this.dispatchAddToCartClick(addToCartButton);
      if (!await cartResult) return;
      if (checkout) window.location.href = '/checkout';
    } finally {
      this.addingToCart = false;
      button.removeAttribute('aria-busy');
    }
  }

  findAddToCartButton() {
    const root = this.productInformation || document;
    const selectors = [
      'add-to-cart-component button[name="add"]',
      'button[name="add"]',
      '.add-to-cart-button',
    ];
    for (const selector of selectors) {
      const candidates = [...root.querySelectorAll(selector)];
      const button = candidates.find((candidate) =>
        !candidate.closest('[hidden]') && candidate.getClientRects().length) || candidates[0];
      if (button) return button;
    }
    return null;
  }

  waitForAddToCartEnabled(button, timeout = 1500) {
    if (!button.disabled) return Promise.resolve(true);
    return new Promise((resolve) => {
      const startedAt = performance.now();
      const check = () => {
        if (!button.disabled) resolve(true);
        else if (performance.now() - startedAt >= timeout) resolve(false);
        else requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });
  }

  // 主题的 special-request 同步逻辑挂在 pointerdown 捕获阶段, 所以要补发一个 pointerdown
  dispatchAddToCartClick(button) {
    const options = { bubbles: true, cancelable: true, composed: true };
    try {
      button.dispatchEvent(new PointerEvent('pointerdown', options));
    } catch (error) {
      button.dispatchEvent(new MouseEvent('mousedown', options));
    }
    button.click();
  }

  waitForCartResult(timeout = 15000) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (success) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        document.removeEventListener('cart:update', handleUpdate);
        document.removeEventListener('cart:error', handleError);
        resolve(success);
      };
      const handleUpdate = (event) => finish(!event.detail?.data?.didError);
      const handleError = () => finish(false);
      const timer = window.setTimeout(() => finish(false), timeout);
      document.addEventListener('cart:update', handleUpdate);
      document.addEventListener('cart:error', handleError);
    });
  }

  notifyCartError(message) {
    console.error('[CaseStylePicker]', message);
    if (typeof MessageToast !== 'undefined' && typeof MessageToast.error === 'function') {
      MessageToast.error(message, 3000);
    }
  }

  createExportBlob() {
    if (!this.canvas) return Promise.reject(new Error('Unable to export the canvas image.'));
    const maxSide = 1600;
    const sourceWidth = this.canvas.width;
    const sourceHeight = this.canvas.height;
    const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = Math.max(1, Math.round(sourceWidth * scale));
    exportCanvas.height = Math.max(1, Math.round(sourceHeight * scale));
    const exportContext = exportCanvas.getContext('2d');
    if (!exportContext) return Promise.reject(new Error('Unable to export the canvas image.'));
    exportContext.imageSmoothingEnabled = true;
    exportContext.imageSmoothingQuality = 'high';
    exportContext.fillStyle = '#fff';
    exportContext.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportContext.drawImage(this.canvas, 0, 0, exportCanvas.width, exportCanvas.height);
    return new Promise((resolve, reject) => {
      try {
        exportCanvas.toBlob(
          (result) => result ? resolve(result) : reject(new Error('Unable to export the canvas image.')),
          'image/jpeg',
          0.82,
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  async getUploadToken() {
    const readToken = () => {
      try {
        return localStorage.getItem('deliver3d_guest_token') || window.guestToken || '';
      } catch (error) {
        return window.guestToken || '';
      }
    };
    let token = readToken();
    if (token) return token;
    const result = await window.GuestAccount?.init?.();
    token = result?.token || readToken();
    return token;
  }

  replaceUploadPhoto(url, uploadRoot) {
    if (!uploadRoot) throw new Error('The Upload Photo field could not be found.');
    const generatedInput = uploadRoot.querySelector('[data-ai-generated-image-input]');
    const displayInput = uploadRoot.querySelector('[data-ai-generated-display-input]');
    const jobIdInput = uploadRoot.querySelector('[data-ai-generated-job-id-input]');
    const trigger = uploadRoot.querySelector('[data-ai-upload-trigger]');
    const preview = trigger?.querySelector('.ai-upload-preview');

    if (generatedInput) generatedInput.value = url;
    if (displayInput) displayInput.value = url;
    if (jobIdInput) jobIdInput.value = '';
    if (preview) preview.src = url;
    trigger?.classList.add('has-image');
    uploadRoot.dispatchEvent(new CustomEvent('casecase:canvas-design-saved', {
      bubbles: true,
      detail: { url },
    }));
  }

  exportImage() {
    if (!this.canvas || !Object.values(this.layers).some((layer) => layer.image?.complete && layer.image.naturalWidth)) return;
    let exportPromise;
    try {
      exportPromise = this.createExportBlob();
    } catch (error) {
      console.warn('Unable to export the canvas image.', error);
      return;
    }
    exportPromise.then((blob) => {
      const link = document.createElement('a');
      link.download = 'casecase-design.jpg';
      link.href = URL.createObjectURL(blob);
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    }).catch((error) => {
      console.warn('Unable to export the canvas image.', error);
    });
  }

  updateVisibility() {
    const visible = Boolean(this.layers.generated.url);
    if (visible && this.overlay.parentElement !== this.media) this.media.append(this.overlay);
    if (visible && this.transformVisible === undefined) this.transformVisible = true;
    this.overlay.hidden = !visible;
    this.overlay.setAttribute('aria-hidden', String(!visible));
    if (this.transformBox) {
      this.transformBox.hidden = !visible || !this.transformVisible;
      this.transformBox.setAttribute('aria-hidden', String(!visible || !this.transformVisible));
    }
    if (this.controls) this.controls.hidden = !visible;
    if (visible) {
      requestAnimationFrame(this.resizeCanvas);
    }
  }

  resizeCanvas = () => {
    if (this.overlay.hidden) return;
    const width = this.overlay.clientWidth;
    const height = this.overlay.clientHeight;
    if (!width || !height) return;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.round(width * this.pixelRatio);
    const targetHeight = Math.round(height * this.pixelRatio);
    if (this.canvas.width !== targetWidth || this.canvas.height !== targetHeight) {
      this.canvas.width = targetWidth;
      this.canvas.height = targetHeight;
    }
    this.draw();
  };

  drawLayer(layerName, width, height, fitMode, targetContext = this.context) {
    const layer = this.layers[layerName];
    if (!layer.image?.complete || !layer.image.naturalWidth) return null;
    const frame = layerName === 'generated' && this.generatedFrame
      ? this.generatedFrame
      : { left: 0, top: 0, width, height };
    const fit = fitMode === 'cover'
      ? Math.max(frame.width / layer.image.naturalWidth, frame.height / layer.image.naturalHeight)
      : Math.min(frame.width / layer.image.naturalWidth, frame.height / layer.image.naturalHeight);
    const drawWidth = layer.image.naturalWidth * fit * (layer.scaleX ?? layer.scale);
    const drawHeight = layer.image.naturalHeight * fit * (layer.scaleY ?? layer.scale);
    const left = frame.left + (frame.width - drawWidth) / 2 + layer.offsetX;
    const top = frame.top + (frame.height - drawHeight) / 2 + layer.offsetY;
    if (targetContext) {
      targetContext.drawImage(
        layer.image,
        left,
        top,
        drawWidth,
        drawHeight
      );
    }
    return { left, top, width: drawWidth, height: drawHeight, right: left + drawWidth, bottom: top + drawHeight };
  }

  isEditableLayer(layerName) {
    return layerName === 'generated';
  }

  updateTransformBox() {
    const confirmButton = this.overlay?.querySelector('[data-case-style-confirm]');
    if (confirmButton) confirmButton.textContent = this.transformVisible ? 'Confirm' : 'Edit';
    const bounds = this.bounds?.[this.activeLayer];
    if (!this.transformBox || !bounds || !this.isEditableLayer(this.activeLayer) || this.overlay.hidden || !this.transformVisible) {
      if (this.transformBox) this.transformBox.hidden = true;
      this.canvas?.classList.add('is-transform-hidden');
      return;
    }
    this.transformBox.hidden = false;
    this.canvas.classList.remove('is-transform-hidden');
    this.transformBox.style.left = `${bounds.left}px`;
    this.transformBox.style.top = `${bounds.top}px`;
    this.transformBox.style.width = `${bounds.width}px`;
    this.transformBox.style.height = `${bounds.height}px`;
  }

  draw() {
    if (!this.context || !this.canvas) return;
    const width = this.canvas.width / this.pixelRatio;
    const height = this.canvas.height / this.pixelRatio;
    this.context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
    this.context.clearRect(0, 0, width, height);
    this.context.fillStyle = '#fff';
    this.context.fillRect(0, 0, width, height);
    const styleBounds = this.layers.style.image?.complete && this.layers.style.image.naturalWidth
      ? this.drawLayer('style', width, height, 'contain', null)
      : null;
    if (styleBounds && this.styleContext) {
      if (this.styleCanvas.width !== this.canvas.width || this.styleCanvas.height !== this.canvas.height) {
        this.styleCanvas.width = this.canvas.width;
        this.styleCanvas.height = this.canvas.height;
      }
      this.styleContext.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      this.styleContext.clearRect(0, 0, width, height);
      this.drawLayer('style', width, height, 'contain', this.styleContext);
      const styleMaskKey = [this.layers.style.url, this.styleCanvas.width, this.styleCanvas.height, styleBounds.left, styleBounds.top, styleBounds.width, styleBounds.height].join('|');
      if (this.styleMaskCacheKey !== styleMaskKey) {
        this.buildStyleMask();
        this.styleMaskCacheKey = styleMaskKey;
      }
    }
    const placementBounds = styleBounds && this.backplateBounds
      ? this.backplateBounds
      : (styleBounds || { left: 0, top: 0, width, height });
    this.updateGeneratedFrame(placementBounds);
    let generatedBounds = this.layers.generated.image?.complete && this.layers.generated.image.naturalWidth
      ? this.drawLayer('generated', width, height, 'cover', null)
      : null;
    if (generatedBounds && styleBounds && this.clampGeneratedPosition(generatedBounds, styleBounds)) {
      generatedBounds = this.drawLayer('generated', width, height, 'cover', null);
    }
    this.bounds = { generated: generatedBounds, style: styleBounds };
    if (generatedBounds && styleBounds && this.maskContext) {
      if (this.maskCanvas.width !== this.canvas.width || this.maskCanvas.height !== this.canvas.height) {
        this.maskCanvas.width = this.canvas.width;
        this.maskCanvas.height = this.canvas.height;
      }
      this.maskContext.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      this.maskContext.clearRect(0, 0, width, height);
      this.drawLayer('generated', width, height, 'cover', this.maskContext);
      this.maskContext.globalCompositeOperation = 'destination-in';
      this.maskContext.drawImage(this.styleMaskCanvas, 0, 0, width, height);
      this.maskContext.globalCompositeOperation = 'destination-out';
      this.maskContext.drawImage(this.styleCanvas, 0, 0, width, height);
      this.maskContext.globalCompositeOperation = 'source-over';
      this.context.drawImage(this.maskCanvas, 0, 0, width, height);
    } else if (generatedBounds) {
      this.drawLayer('generated', width, height, 'cover');
    }
    if (styleBounds) {
      this.context.drawImage(this.styleCanvas, 0, 0, width, height);
    }
    this.updateTransformBox();
  }

  updateGeneratedFrame(bounds) {
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return;
    const previous = this.generatedFrame;
    const next = { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height };
    if (previous && previous.width > 0 && previous.height > 0) {
      const layer = this.layers.generated;
      layer.offsetX *= next.width / previous.width;
      layer.offsetY *= next.height / previous.height;
    }
    this.generatedFrame = next;
  }

  findBackplateBounds(data, exterior, width, height) {
    const visited = new Uint8Array(width * height);
    const stack = [];
    let largest = null;
    const push = (position) => {
      if (visited[position] || exterior[position] || data[position * 4 + 3] > 10) return;
      visited[position] = 1;
      stack.push(position);
    };
    for (let start = 0; start < visited.length; start += 1) {
      if (visited[start] || exterior[start] || data[start * 4 + 3] > 10) continue;
      push(start);
      let count = 0;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      while (stack.length) {
        const position = stack.pop();
        const x = position % width;
        const y = Math.floor(position / width);
        count += 1;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        if (x > 0) push(position - 1);
        if (x < width - 1) push(position + 1);
        if (y > 0) push(position - width);
        if (y < height - 1) push(position + width);
      }
      if (!largest || count > largest.count) largest = { count, minX, minY, maxX, maxY };
    }
    if (!largest || largest.count < 16) return null;
    const ratio = this.pixelRatio || 1;
    const bleed = 1;
    return {
      left: largest.minX / ratio - bleed,
      top: largest.minY / ratio - bleed,
      width: (largest.maxX - largest.minX + 1) / ratio + bleed * 2,
      height: (largest.maxY - largest.minY + 1) / ratio + bleed * 2,
    };
  }

  buildStyleMask() {
    this.backplateBounds = null;
    if (!this.styleContext || !this.styleMaskContext || !this.styleCanvas.width || !this.styleCanvas.height) return;
    let pixels;
    try {
      pixels = this.styleContext.getImageData(0, 0, this.styleCanvas.width, this.styleCanvas.height);
    } catch (error) {
      console.warn('Unable to process the Case Style image.', error);
      return;
    }
    if (this.styleMaskCanvas.width !== this.styleCanvas.width || this.styleMaskCanvas.height !== this.styleCanvas.height) {
      this.styleMaskCanvas.width = this.styleCanvas.width;
      this.styleMaskCanvas.height = this.styleCanvas.height;
    }
    const { data } = pixels;
    const width = this.styleCanvas.width;
    const height = this.styleCanvas.height;
    const exterior = new Uint8Array(width * height);
    const stack = [];
    const pushIfTransparent = (x, y) => {
      const position = y * width + x;
      if (exterior[position] || data[position * 4 + 3] > 10) return;
      exterior[position] = 1;
      stack.push(position);
    };
    for (let x = 0; x < width; x += 1) {
      pushIfTransparent(x, 0);
      pushIfTransparent(x, height - 1);
    }
    for (let y = 1; y < height - 1; y += 1) {
      pushIfTransparent(0, y);
      pushIfTransparent(width - 1, y);
    }
    while (stack.length) {
      const position = stack.pop();
      const x = position % width;
      const y = Math.floor(position / width);
      if (x > 0) pushIfTransparent(x - 1, y);
      if (x < width - 1) pushIfTransparent(x + 1, y);
      if (y > 0) pushIfTransparent(x, y - 1);
      if (y < height - 1) pushIfTransparent(x, y + 1);
    }
    const mask = new ImageData(width, height);
    for (let position = 0; position < width * height; position += 1) {
      mask.data[position * 4 + 3] = exterior[position] ? 0 : 255;
    }
    this.styleMaskContext.putImageData(mask, 0, 0);
    this.backplateBounds = this.findBackplateBounds(data, exterior, width, height);
  }
}

if (!customElements.get('case-style-picker-component')) {
  customElements.define('case-style-picker-component', CaseStylePicker);
}
